import React, { useContext } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { AuthContext } from '@/context/auth-context'
import AccountIcon from '@/assets/icons/user-account.svg?react'
import CreditCardIcon from '@/assets/icons/credit-card.svg?react'
import SettingsIcon from '@/assets/icons/settings.svg?react'
import MessageDotsIcon from '@/assets/icons/message-dots.svg?react'
import LogOutIcon from '@/assets/icons/log-out.svg?react'
import { ArrowUpRight, Diamond, Tools, DropdownTriangle, EnterpriseOrganization } from '@/assets/icons'
import { kProduction } from '@/constants/constants-ui'
import { useNavigate, useMatch } from 'react-router-dom'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import signOutOfApp from '@/firebase/auth/sign-out'
import { BrandEnum } from '@/firebase/auth/auth-jwt-schema'
import { getHelpCenterUrl } from '@/util/enterprise'
import { kLinkPrivacyPolicyUrl, kLinkTermsOfServiceUrl } from '@/constants/constants-links'
import { ClockStopwatch } from '@/assets/icons'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { SubscriptionStatus } from '@/firebase/auth/auth-jwt-schema'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { setReferralDialogVisible, setMobileSidebarOpen } from '@/store/slices/ui-state.slice'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { RootState } from '@/store/store'

type AccountMenuItemType = {
  name: string
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  current: boolean
  onClick: () => void | Promise<void>
  hideFromAnon?: boolean
  excludedBrands?: string[]
}

const SidebarAccountMenu: React.FC = () => {
  const navigate = useNavigate()
  const { toggleWidget } = useHubspotConversations()
  const { user, userAccountData } = useContext(AuthContext)
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // DEV ONLY: Testing variables - set to true to test different states
  const devOverrides = {
    forceTrial: false, // Set to true to force trial state
    forceBrand: null as BrandEnum | null, // Set to BrandEnum.HAIKU to test enterprise brand
    forceStudent: false, // Set to true to test student account
    forceReferral: false, // Set to true to force show referral dialog
  }

  // Redux state
  const hasLegacyStudentPlan = useAppSelector((state: RootState) => state.accountState.hasLegacyStudentPlan)

  // Override values for testing if in development
  const subscriptionStatus = !kProduction && devOverrides.forceTrial ? SubscriptionStatus.TRIAL : userAccountData?.subscriptionStatus
  const brand = !kProduction && devOverrides.forceBrand ? devOverrides.forceBrand : userAccountData?.brand ?? BrandEnum.NONE
  const userIsAnon = user?.isAnonymous
  const showStudentPlan = !kProduction && devOverrides.forceStudent ? true : hasLegacyStudentPlan
  const isPaidSubscriber = userAccountData?.subscriptionStatus === SubscriptionStatus.PAID
  const isOrgAccount = userAccountData?.isOrgAccount
  const showReferral = !kProduction && devOverrides.forceReferral ? true : !userIsAnon && brand === BrandEnum.PAXTON && !isOrgAccount && isPaidSubscriber

  const legacyCompatibleOrgId = userAccountData?.legacyDriveCompatibleOrganizationId
  const isOrgAdmin = userAccountData?.isOrgAdmin
  const maskSentry = userAccountData?.brand == BrandEnum.HAIKU

  // Add route matching
  const accountMatch = useMatch('/dashboard/account')
  const adminMatch = useMatch('/dashboard/admin')
  const settingsMatch = useMatch('/dashboard/settings')
  // This is for logged in users only
  if (!user || userIsAnon) {
    return null
  }

  // Menu elements
  const menuElements: AccountMenuItemType[] = [
    ...(!isOrgAccount
      ? [
          {
            hideFromAnon: true,
            name: 'Manage Subscription',
            icon: CreditCardIcon,
            current: accountMatch !== null,
            onClick: () => {
              navigate('/dashboard/account')
            },
          },
        ]
      : []),
    ...(legacyCompatibleOrgId && isOrgAdmin
      ? [
          {
            hideFromAnon: true,
            name: 'Admin Dashboard',
            icon: EnterpriseOrganization,
            current: adminMatch !== null,
            onClick: () => {
              navigate('/dashboard/admin')
            },
          },
        ]
      : []),
    {
      name: 'Settings',
      icon: SettingsIcon,
      current: settingsMatch !== null,
      onClick: () => {
        navigate('/dashboard/settings')
      },
    },
    {
      hideFromAnon: false,
      name: 'Chat with support',
      icon: MessageDotsIcon,
      current: false,
      onClick: () => {
        toggleWidget()
      },
      excludedBrands: [BrandEnum.HAIKU],
    },
    {
      hideFromAnon: true,
      name: 'Sign Out',
      icon: LogOutIcon,
      current: false,
      onClick: () => {
        signOutOfApp(navigate)
      },
    },
    ...(!kProduction
      ? [
          {
            name: 'Log Token (dev only)',
            icon: Tools,
            current: false,
            onClick: async () => {
              const token = await user.getIdToken(true) // force refresh true
              console.log(token)
            },
          },
        ]
      : []),
  ].filter((item) => ((userIsAnon && !item.hideFromAnon) || !userIsAnon) && !item.excludedBrands?.includes(brand))

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <div className="py-1 pl-3 pr-1 border-t">
          <button
            className="w-[239px] h-10 group flex items-center gap-x-2.5 pl-1 pr-3 py-2 rounded-full hover:bg-brand-neutral-100 focus:outline-none"
            aria-label="Account Menu"
          >
            <div className="flex-none flex justify-center items-center w-8 h-8 rounded-full bg-brand-neutral-200 group-hover:bg-brand-neutral-300">
              {user.displayName ? (
                <span className="text-brand-neutral-700 font-medium text-base">
                  {user.displayName
                    .split(' ')
                    .map((name) => name[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </span>
              ) : (
                <AccountIcon className="w-5 h-5 text-brand-neutral-700" />
              )}
            </div>
            <span className={`flex-grow text-left text-xs leading-5 font-medium truncate ${maskSentry ? ' sentry-mask' : ''}`}>
              {user.displayName ?? user.email}
            </span>
            <DropdownTriangle className="w-4 h-4 text-brand-neutral-950" />
          </button>
        </div>
      </DropdownMenu.Trigger>

      <MenuContent className="w-[239px] border-brand-neutral-300" sideOffset={-1} align="end" alignOffset={-1}>
        {subscriptionStatus === SubscriptionStatus.TRIAL && (
          <div className="flex items-center gap-1.5 px-4 py-2 bg-brand-50 border-b border-brand-100">
            <ClockStopwatch className="w-5 h-5 text-brand-500" />
            <span className="text-[14px] font-semibold leading-[1.429em] tracking-[-1.07%] text-brand-500">Free Trial</span>
          </div>
        )}
        <div className="py-4 px-4">
          <div className="flex flex-col gap-4">
            <span className={`text-[14px] leading-5 font-medium text-brand-neutral-600 truncate ${maskSentry ? ' sentry-mask' : ''}`}>
              {user.displayName ?? user.email}
            </span>
            {isOrgAccount && userAccountData?.orgData?.name && brand === BrandEnum.PAXTON && (
              <span className="text-xs leading-4 font-medium text-brand-neutral-700 uppercase">{userAccountData.orgData.name}</span>
            )}
            {brand !== BrandEnum.NONE && brand !== BrandEnum.PAXTON ? (
              <>
                <span className="text-xs leading-4 font-medium text-brand-neutral-700 uppercase">{brand === BrandEnum.HAIKU ? 'Holland & Knight' : brand}</span>
                <div className="flex items-center gap-2">
                  <span className="px-1 py-[1px] bg-brand-neutral-950 rounded text-[12px] leading-4 text-white font-medium uppercase">Enterprise</span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <span className="px-1 py-[1px] bg-brand-neutral-950 rounded text-[12px] leading-4 text-white font-medium uppercase">
                  {showStudentPlan ? 'Student' : 'Professional'}
                </span>
              </div>
            )}
          </div>
        </div>

        <MenuSeparator />
        <MenuItemGroup>
          {menuElements.map((item) => (
            <MenuItem
              key={item.name}
              label={item.name}
              icon={item.icon}
              selected={!!item.current}
              onSelect={() => {
                item.onClick?.()
                dispatch(setMobileSidebarOpen(false))
              }}
            />
          ))}
        </MenuItemGroup>

        <MenuSeparator />
        <MenuItemGroup>
          <MenuItem
            label="Help Center"
            trailingIcon={ArrowUpRight}
            onSelect={() => {
              window.open(getHelpCenterUrl(), '_blank')
              dispatch(setMobileSidebarOpen(false))
            }}
          />
          {/* Hide Privacy Policy and Terms of Service for enterprise users */}
          {(brand === BrandEnum.NONE || brand === BrandEnum.PAXTON) && (
            <MenuItem
              label="Privacy Policy"
              trailingIcon={ArrowUpRight}
              onSelect={() => {
                window.open(kLinkPrivacyPolicyUrl, '_blank')
                dispatch(setMobileSidebarOpen(false))
              }}
            />
          )}
          {(brand === BrandEnum.NONE || brand === BrandEnum.PAXTON) && (
            <MenuItem
              label="Terms of Service"
              trailingIcon={ArrowUpRight}
              onSelect={() => {
                window.open(kLinkTermsOfServiceUrl, '_blank')
                dispatch(setMobileSidebarOpen(false))
              }}
            />
          )}
        </MenuItemGroup>

        <MenuSeparator />
        <div>
          {showReferral && (
            <div className="p-4" onClick={(e) => e.stopPropagation()}>
              <div className="w-full bg-brand-50 border border-brand-100 rounded-[4px] p-3">
                <div className="flex flex-col gap-2">
                  <p className="text-brand-neutral-700 text-xs leading-[1.333em] font-normal">
                    Invite others to join Paxton and earn 1 free month per referral.
                  </p>
                  <div className="flex justify-center items-center gap-1">
                    <Diamond className="w-4 h-4 text-brand-500" />
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        dispatch(setMobileSidebarOpen(false))
                        dispatch(setReferralDialogVisible(true))
                        trackEvent(AnalyticsEvent.ReferralDialogOpened)
                      }}
                      className="text-brand-500 text-xs leading-[1.333em] font-semibold hover:opacity-80"
                    >
                      Get 1 Month Free
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="px-4 py-2 bg-brand-neutral-100">
            <span className="block text-[10.4px] leading-[1.077em] text-brand-neutral-700 font-normal">v{APP_VERSION}</span>
          </div>
        </div>
      </MenuContent>
    </DropdownMenu.Root>
  )
}

export default SidebarAccountMenu
